import { useMemo } from "react";
import { CreatomatePropertyValue, ElementSource } from "chq";
import { sleep, t } from "app/utils";
import { ElementState, Preview } from "@creatomate/preview";
import { ANIMATIONS, DEFAULT_ANIMATION_SETTINGS, GENERIC_ANIMATIONS, TEXT_ANIMATIONS } from "components/stories/video-studio/utils/constants";
import general from "constants/general";
import { Context, HandleSelectAnimationProps, Mode, Subtype, Time, TRANSITION_ANIMATION_SUBTRACTION } from "./AnimationTypes";
import { isSubtitleText, modificationsForAllSubtitles } from "../tabs/subtitle/utils";
import { getRgbColor } from "../../utils";

type Animation = Record<string, any>;

const animationTimesByMode = {
  [Mode.Enter]: "start",
  [Mode.Exit]: "end",
  [Mode.Transition]: "start"
} as Record<Mode, string>;
export const ANIMATION_TIME = 1;

const useAnimation = (
  mode: Mode,
  context: Context,
  preview: Preview,
  activeElement: ElementState
) => {
  const { source: { animations: activeElementAnimations } } = activeElement;
  const { elements }: {elements: ElementState[]} = preview.state || { elements: [] };

  const subtitleElements = useMemo(() => {
    if (context !== Context.Subtitle) return [];

    return elements.filter(element => isSubtitleText(element.source.name));
  }, [context, elements]);

  const prevElement: ElementState | undefined = useMemo(() => {
    if (mode !== Mode.Transition) return;

    const filteredElements = elements.filter(element => element.track === activeElement.track
      && element.globalTime < activeElement.globalTime
      && !isSubtitleText(element.source.name));

    // eslint-disable-next-line consistent-return
    return filteredElements.sort((a, b) => b.globalTime - a.globalTime)[0];
  }, [activeElement, elements, mode]);

  const getAnimations = () => {
    const animationContexts = {
      [Context.Media]: GENERIC_ANIMATIONS,
      [Context.Text]: TEXT_ANIMATIONS,
      [Context.Subtitle]: TEXT_ANIMATIONS
    };
    const animations = [
      {
        label: t("None"),
        value: ANIMATIONS.NONE
      } as Animation
    ].concat(animationContexts[context] as Animation[]);
    if (mode === Mode.Transition) {
      return animations.filter(
        a => a.value !== ANIMATIONS.POP && a.value !== ANIMATIONS.FLOAT
      );
    }
    return animations;
  };

  const getAnimationTime = () => animationTimesByMode[mode];

  const getSelectedAnimation = (
    time: HandleSelectAnimationProps["time"]
  ) => {
    const isTransitionAnimation = (keyframe: ElementSource) => (
      time === animationTimesByMode.transition && keyframe.time === 0 && keyframe.transition
    );

    const isEnterAnimation = (keyframe: ElementSource) => (
      time === animationTimesByMode.enter && keyframe.time === 0 && !keyframe.transition
    );

    const isExitAnimation = (keyframe: ElementSource) => (
      time === animationTimesByMode.exit && keyframe.time === time
    );

    const animation = activeElementAnimations?.find((keyframe: ElementSource) => {
      if (mode === "transition") return isTransitionAnimation(keyframe);
      return isEnterAnimation(keyframe) || isExitAnimation(keyframe);
    });

    return animation;
  };

  const handleSelectAnimation = async ({
    time,
    type,
    subtype = Object.keys(DEFAULT_ANIMATION_SETTINGS[type])[0] as Subtype,
    duration = ANIMATION_TIME,
    color = general.DEFAULT_COLOR
  }: HandleSelectAnimationProps) => {
    const isNoneAnimation = type === ANIMATIONS.NONE;

    const translateSpecialType = (newType: string) => {
      if (newType === ANIMATIONS.FLOAT) return ANIMATIONS.SLIDE;
      if (newType === ANIMATIONS.POP) return ANIMATIONS.SCALE;
      return newType;
    };

    const handleSubtitle = async (
      propertyName: string,
      value: Animation[]
    ) => {
      await preview.applyModifications(
        modificationsForAllSubtitles(
          subtitleElements,
          propertyName,
          value as CreatomatePropertyValue
        )
      );
    };

    const handleTransition = async (isNone: boolean): Promise<ElementSource> => {
      if (!prevElement) return {}; // Early return if prevElement is not defined
      if (isNone) {
        const { source: { animations: currentAnimations } } = activeElement;
        const newAnimations = removeExistingAnimationFromList("start", currentAnimations);
        const newTime = prevElement.globalTime + prevElement.duration;
        return {
          [`${activeElement.source.id}.animations`]: newAnimations,
          [`${activeElement.source.id}.time`]: newTime
        };
      }

      const { source: { animations: prevElementAnimations } } = prevElement;
      const newAnimations = removeExistingAnimationFromList("end", prevElementAnimations);
      const transitionTime = prevElement.globalTime + prevElement.duration
      - (duration - TRANSITION_ANIMATION_SUBTRACTION);
      return {
        [`${prevElement.source.id}.animations`]: newAnimations,
        [`${activeElement.source.id}.time`]: transitionTime
      };
    };

    const removeExistingAnimationFromList = (
      time: Time,
      animations: Animation[]
    ) => animations?.filter(
      keyframe => !(!keyframe.time && time === "start") && keyframe.time !== time) ?? [];

    const playAnimation = async () => {
      const startTime = prevElement
        ? prevElement.globalTime + prevElement.duration
        - (duration - TRANSITION_ANIMATION_SUBTRACTION)
        : activeElement.globalTime;
      const endTime = activeElement.duration || preview.state?.duration || 0;
      if (time === "end") preview.setTime(startTime + endTime - duration);
      else preview.setTime(startTime);
      await preview.play();
      await sleep(duration * 1000);
      await preview.pause();
    };

    const setAnimation = async () => {
      const newAnimations = removeExistingAnimationFromList(time, activeElementAnimations);

      if (!isNoneAnimation) {
        const animation: Animation = {
          ...DEFAULT_ANIMATION_SETTINGS[type][subtype],
          type: translateSpecialType(type),
          time,
          duration,
          color: getRgbColor(color)
        };

        // Reverse animation when used as exit animation
        if (time === "end") {
          animation.reversed = true;
        }

        if (mode === Mode.Transition) {
          animation.transition = true;
        }

        newAnimations.push(animation);
      }

      if (context === Context.Subtitle) {
        await handleSubtitle("animations", newAnimations);
      } else {
        const modifications: ElementSource = {
          [`${activeElement.source.id}.animations`]: newAnimations.map(animation => ({
            ...animation,
            type: translateSpecialType(animation.type)
          })),
          ...(mode === Mode.Transition ? await handleTransition(isNoneAnimation) : {})
        };
        await preview.applyModifications(modifications);
      }

      await playAnimation();
    };

    await setAnimation();
  };

  return {
    getAnimations,
    getAnimationTime,
    getSelectedAnimation,
    handleSelectAnimation
  };
};

export default useAnimation;
